import React from 'react'
import {
    AllEvents,
    createEditionSelectItemEvent,
    formatEditionTitle,
    Product,
    ValidCategories,
} from '@news-mono/web-common'
import { NightlyButton } from '../../../../buttons/NightlyButton/NightlyButton'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'

export const EditionsNightlyButtons = ({
    edition,
    heading,
    onEvent,
}: {
    edition: EditionV4DTO
    heading?: string
    onEvent: (event: AllEvents) => void
}) => {
    const normalizedEditionTitle =
        heading || formatEditionTitle(edition.title) || ''

    const handleButtonClick = ({
        collectionName,
        category,
        linkText,
        linkUrl,
    }: {
        collectionName: string
        category: ValidCategories
        linkText: string
        linkUrl: string
    }) => {
        onEvent(
            createEditionSelectItemEvent({
                edition,
                collectionName,
                product: Product.TheNightly,
                itemDetails: {
                    category,
                    linkText,
                    linkUrl,
                    imageUrl: edition.mainImages?.[0]?.src,
                },
            }),
        )
    }

    const buttonProps = {
        variant: 'default',
        color: 'primary',
        fill: 'filled',
        layout: {
            shouldGrow: true,
            alignSelf: 'stretch',
        },
    } as const

    const { primaryButton, secondaryButton } = getButtonProps({
        edition,
        normalizedEditionTitle,
    })

    if (!primaryButton || !secondaryButton) {
        return null
    }

    const primaryButtonProps = {
        ...buttonProps,
        text: primaryButton.text,
        action: {
            type: 'link',
            to: primaryButton.to,
            onClick: () =>
                handleButtonClick({
                    collectionName: primaryButton.collectionName,
                    category: primaryButton.category,
                    linkText: primaryButton.linkText,
                    linkUrl: primaryButton.linkUrl,
                }),
        },
    } as const

    const secondaryButtonProps = {
        ...buttonProps,
        fill: 'outline',
        text: secondaryButton.text,
        action: {
            type: 'link',
            to: secondaryButton.to,
            onClick: () =>
                handleButtonClick({
                    collectionName: secondaryButton.collectionName,
                    category: secondaryButton.category,
                    linkText: secondaryButton.linkText,
                    linkUrl: secondaryButton.linkUrl,
                }),
        },
    } as const

    return (
        <>
            <NightlyButton {...primaryButtonProps} />
            <NightlyButton {...secondaryButtonProps} />
        </>
    )
}

interface ButtonProps {
    text: string
    to: string
    fill?: 'filled' | 'outline'
    collectionName: string
    category: ValidCategories
    linkText: string
    linkUrl: string
}

interface NightlyButtonProps {
    primaryButton?: ButtonProps
    secondaryButton?: ButtonProps
}

const getButtonProps = ({
    edition,
    normalizedEditionTitle,
}: {
    edition: EditionV4DTO
    normalizedEditionTitle: string
}): NightlyButtonProps => {
    switch (edition.publication) {
        case 'the_nightly_travel':
            return {
                primaryButton: {
                    text: 'Read the Latest Edition',
                    to: edition.editionWebUrl,
                    collectionName: 'TheNightlyTravel_ReadEditionButton',
                    category: 'thenightly_travel_cta',
                    linkText: 'Read the Latest Edition',
                    linkUrl: edition.editionWebUrl,
                },
                secondaryButton: {
                    text: 'All Editions',
                    to: '/lifestyle/travel/editions',
                    collectionName: 'TheNightlyTravel_MoreArticlesButton',
                    category: 'thenightly_travel_cta',
                    linkText: 'All Editions',
                    linkUrl: '/lifestyle/travel/editions',
                },
            }
        case 'the_nightly_on':
            return {
                primaryButton: {
                    text: normalizedEditionTitle,
                    to: edition.editionWebUrl,
                    collectionName: 'TheNightlyOn_ReadEditionButton',
                    category: 'thenightly_on_cta',
                    linkText: 'Read the Latest Edition',
                    linkUrl: normalizedEditionTitle,
                },
                secondaryButton: {
                    text: 'More Articles',
                    to: edition.primaryTopic
                        ? `${edition.primaryTopic}`
                        : '/on',
                    collectionName: 'TheNightlyOn_MoreArticlesButton',
                    category: 'thenightly_on_cta',
                    linkText: 'More Articles',
                    linkUrl: edition.primaryTopic
                        ? `${edition.primaryTopic}`
                        : '/on',
                },
            }
        default:
            return {
                primaryButton: {
                    text: normalizedEditionTitle,
                    to: edition.editionWebUrl,
                    collectionName: 'TheNightlyOn_ReadTheLatestEditionButton',
                    category: 'thenightly_editions_page_latest_editions',
                    linkText: 'Read the Latest Edition',
                    linkUrl: edition.editionWebUrl,
                },
                secondaryButton: {
                    text: 'All Editions',
                    to: '/editions',
                    collectionName: 'TheNightly_AllEditionsButton',
                    category: 'thenightly_editions_page_latest_editions',
                    linkText: 'All Editions',
                    linkUrl: '/editions',
                },
            }
    }
}
